'use client';

/**
 * ContentFaqGridCard — the FAQ layout: the selected topic's questions as a
 * GRID of compact answer cards (6-10 in view). Placed full-bleed by the
 * `mode10_faq` preset; the preset choosing THIS card is what makes the
 * layout a grid (no runtime branch). mode9 pattern: thin, reads the shared
 * use-content-set hook + the selected topic.
 *
 * @see klyde_edge/ui_config/composition/cards/investor_relations.py — content_key="content_faq_grid"
 */

import { useMemo } from 'react';
import { Loader2, XCircle, Landmark } from 'lucide-react';
import { CardPlacementProvider, useCardExport } from '@/lib/design/card-export';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useExplorerLabels } from '@/lib/stores/total-matrix-store';
import { cn } from '@/lib/utils/cn';
import { text, surfaceBase } from '@/lib/design/tokens';
import { useContentSet, findSelectedTopic } from './use-content-set';
import { ContentAnswerCard } from './content-answer-card';

export function ContentFaqGridCard() {
  const L = useExplorerLabels('mode10');
  const sel = useGraphSelectionStore((s) => s.selectedNode);
  const { domains, isLoading, error, queryKey } = useContentSet();
  const found = findSelectedTopic(domains, sel);

  useCardExport(useMemo(() => ({
    label: found?.topic.label ?? (L.tree_title ?? ''),
    content_key: 'content_faq_grid',
    surface: 'canvas' as const,
    status: (isLoading ? 'loading' : found ? 'loaded' : 'idle') as 'loading' | 'loaded' | 'idle',
    query_key: queryKey,
    text: found?.topic.label ?? '',
    json: found ? { domain: found.domain.domain, topic: found.topic.topic, count: found.topic.count } : null,
  }), [found, isLoading, queryKey, L.tree_title]));

  if (isLoading) {
    return <div className="h-full flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }
  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-destructive" style={{ fontSize: surfaceBase.canvas }}>
        <XCircle className="h-8 w-8 mb-2 opacity-50" /><p className={text.body}>{L.tree_error}</p>
      </div>
    );
  }
  if (!found) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-muted-foreground" style={{ fontSize: surfaceBase.canvas }}>
        <Landmark className="h-10 w-10 mb-2 opacity-40" /><p className={text.body}>{L.answer_empty}</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-4" style={{ fontSize: surfaceBase.canvas }}>
      <h2 className={cn(text.heading, 'mb-4')}>{found.topic.label}</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {found.topic.questions.map((q) => (
          <CardPlacementProvider key={q.key} value={{ surface: 'canvas', slot_id: `content_answer:${q.key}` }}>
            <ContentAnswerCard entry={q} queryKey={queryKey} />
          </CardPlacementProvider>
        ))}
      </div>
    </div>
  );
}
